import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/store.ts';
import {
  CarInformationSelector,
  CurrentStepContentSelector,
  CurrentStepDataSelector,
  CurrentStepSelector,
  InitialDataSelector,
  ProductDetailsSelector,
  RooftopTentsRackAddToCartButtonSelector,
  RooftopTentsRackPriceContainerSelector,
  ShowMoreInfoSelector,
  TranslationsSelector,
} from '../../Selectors/FitAssistantSelectors';
import {
  addCarInformation,
  selectOption,
  setFooterIsFixed,
  setHasAddedRooftopTentsRoofRack,
  setShowFitAssistant,
  setRooftopTentsRackAtcCache,
  setShowResetButtons,
  setStepEnum,
  unselectOptions,
} from '../../Slices/FitAssistantSlice.ts';
import { CarInformation, StepDataData } from '../../Types/FitAssistantData.ts';
import { getOptions, getProducts } from '../../Thunks/Queries.ts';
import { ProductSearchType } from '../../Enums/ProductSearchType.ts';
import { FooterHelper } from '../../Helpers/MoreInfoHelper.ts';
import {
  getTentWeightForMatchingProducts,
  rackOptionSupportsTentWeight,
} from '../../Helpers/RooftopTentWeightHelper.ts';
import { MatchingProductsSelector } from '../../Selectors/MatchingProductsSelectors.ts';
import { trackChooseRoofRack, trackClose, trackView } from '../../Analytics/Adobe.ts';
import Buttons from '../Buttons/Buttons.tsx';
import RackOptionItem from './RackOptionItem.tsx';
import AddToCartButton from '../Buttons/AddToCartButton.tsx';
import FitAssistantStep from '../../Enums/FitAssistantStep.ts';
import PrimaryButton from '../Buttons/PrimaryButton.tsx';

import './RackSelector.less';

const RooftopTentsRackSelector = () => {
  const dispatch = useAppDispatch();
  const carInformation = useAppSelector(CarInformationSelector);
  const currentStepContent = useAppSelector(CurrentStepContentSelector);
  const currentStep = useAppSelector(CurrentStepSelector);
  const currentStepData = useAppSelector(CurrentStepDataSelector);
  const showMoreInfo = useAppSelector(ShowMoreInfoSelector);
  const translations = useAppSelector(TranslationsSelector);
  const matchingProducts = useAppSelector(MatchingProductsSelector);
  const productDetail = useAppSelector(ProductDetailsSelector);
  const initialData = useAppSelector(InitialDataSelector);
  const addToCartButtonData = useAppSelector(RooftopTentsRackAddToCartButtonSelector);
  const priceContainerData = useAppSelector(RooftopTentsRackPriceContainerSelector);

  const isExtranet = window._THULEDATA?.isExtranet ?? false;

  const selectedOptionValue = currentStepData?.data?.find((x) => x.Selected)?.Value;

  const rackOptionsEligibleByWeight = useMemo(() => {
    const tentWeight = getTentWeightForMatchingProducts(matchingProducts ?? undefined, productDetail?.ProductId);
    const data = currentStepData?.data;
    if (!data?.length) return [];
    return data.filter((opt) => rackOptionSupportsTentWeight(opt, tentWeight));
  }, [currentStepData?.data, matchingProducts, productDetail?.ProductId]);

  useEffect(() => {
    if (
      !currentStepData?.data ||
      currentStepData?.data?.length === 0 ||
      !currentStepData?.data?.find((x) => x.Selected)
    ) {
      dispatch(getOptions()).then(() => {
        dispatch(unselectOptions());
      });
    } else {
      if (matchingProducts?.length === 0) dispatch(getProducts(ProductSearchType.RooftopTentsRackSelector));
    }

    trackView(initialData.FitAssistantVariation, currentStep, initialData.IsYearFirst, carInformation, true);
  }, []);

  useEffect(() => {
    const selected = currentStepData?.data?.find((x) => x.Selected);
    if (!selected || isExtranet) return undefined;
    if (addToCartButtonData && priceContainerData) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/sitecore/fitguide/GetFitGuideATCAndPrice', {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            ProductIds: selected.SolutionProducts.map((x) => x.ProductId),
          }),
        });
        const data = await res.json();
        if (!cancelled) {
          dispatch(
            setRooftopTentsRackAtcCache({
              addToCartButton: data.Button,
              priceContainer: data.PriceContainer,
            })
          );
        }
      } catch {
        /* keep disabled fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStepData, selectedOptionValue, addToCartButtonData, priceContainerData, isExtranet, dispatch]);

  useEffect(() => {
    let result = FooterHelper.FooterShouldBeFixed('.rack-selector');
    dispatch(setFooterIsFixed(result));

    return () => {
      dispatch(setFooterIsFixed(false));
    };
  }, [currentStepData]);

  const getCarInformation = (option: StepDataData): CarInformation[] => {
    const configItem = currentStepData?.configItems;
    const variation: CarInformation = {
      parameterName: currentStep,
      configurationItem: configItem ?? '',
      text: option.Text,
      value: option.Value,
    };

    let filteredCarinformation = carInformation.filter((x) => x.parameterName != currentStep);
    let updCarInformation: CarInformation[] = [...filteredCarinformation, variation];
    return updCarInformation;
  };

  const handleOptionClick = (option: StepDataData, index: number): void => {
    if (!option.Selected) {
      dispatch(selectOption(index));
      dispatch(setShowResetButtons(false));
      dispatch(addCarInformation(getCarInformation(option)));
      trackChooseRoofRack(
        initialData.FitAssistantVariation,
        initialData.IsYearFirst,
        currentStep,
        carInformation,
        option.Text
      );
    }
  };

  return (
    <>
      {!showMoreInfo && (
        <>
          <div className="rack-selector d-flex flex-column justify-content-center">
            <div
              className="info-text"
              dangerouslySetInnerHTML={{
                __html: currentStepContent?.Text ?? '',
              }}
            />
            <h5>{translations.selectRoofRack}</h5>
            <div className="rack-options mt-6">
              {rackOptionsEligibleByWeight.map((option) => {
                const originalIndex = currentStepData!.data!.indexOf(option);
                return (
                  <RackOptionItem
                    key={originalIndex}
                    option={option}
                    onClick={() => handleOptionClick(option, originalIndex)}
                  />
                );
              })}
            </div>
          </div>
          <Buttons visible hideShadow>
            {addToCartButtonData && priceContainerData && !isExtranet && (
              <AddToCartButton
                addToCartButtonData={addToCartButtonData}
                priceContainer={priceContainerData}
                onComplete={() => {
                  dispatch(setStepEnum(FitAssistantStep.Confirmed));
                  dispatch(setHasAddedRooftopTentsRoofRack(true));
                  dispatch(setShowFitAssistant(false));
                }}
              />
            )}

            {/* Fallback button for when the add to cart button data is not available */}
            {(!addToCartButtonData || !priceContainerData) && !isExtranet && (
              <PrimaryButton text={translations.addToCart} disabled visible onClick={() => {}} />
            )}
            {isExtranet && (
              <PrimaryButton
                text={translations.close}
                visible
                onClick={() => {
                  trackClose(
                    initialData.FitAssistantVariation,
                    currentStep,
                    initialData.IsYearFirst,
                    carInformation,
                    'Close'
                  );
                  dispatch(setShowFitAssistant(false));
                }}
              />
            )}
          </Buttons>
        </>
      )}
    </>
  );
};

export default RooftopTentsRackSelector;
